import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { portalAPI } from '@/lib/api/portal';
import { createCheckoutSession, createFreeRegistration } from '@/lib/api/checkout';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { formatCurrency } from '@/lib/utils';
import { ShoppingCart, Loader2, AlertCircle } from 'lucide-react';
import type { RegistrationWizardState, Cart, CartItem } from '@/types';

interface CheckoutStepProps {
  wizardState: RegistrationWizardState;
  onCartUpdate: (cart: Cart) => void;
  onComplete: () => void;
  onBack: () => void;
}

export function CheckoutStep({
  wizardState,
  onCartUpdate,
  onComplete,
  onBack,
}: CheckoutStepProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, isSignedIn } = useAuth();

  // Fetch tournaments to get names and prices
  const { data: tournamentsData } = useQuery({
    queryKey: ['tournaments'],
    queryFn: () => portalAPI.getTournaments(),
  });

  // Fetch addons to get names and prices
  const { data: addonsData } = useQuery({
    queryKey: ['addons'],
    queryFn: () => portalAPI.getAddons(),
  });

  // Fetch flow config for tax rate
  const { data: flowConfig } = useQuery({
    queryKey: ['flowConfig'],
    queryFn: () => portalAPI.getFlowConfig(),
  });

  const tournaments = tournamentsData?.docs || [];
  const addons = addonsData?.docs || [];
  const taxRate = flowConfig?.tax?.enabled ? (flowConfig.tax.rate || 0) : 0;

  // Build cart from wizard state
  useEffect(() => {
    const items: CartItem[] = [];

    // Add event registration if paid
    if (wizardState.eventRegistration.entryType &&
        wizardState.eventRegistration.entryType !== 'free' &&
        wizardState.eventRegistration.entryFee) {
      items.push({
        id: 'event-registration',
        type: 'event_registration',
        itemId: 'event',
        name: wizardState.eventRegistration.entryType === 'spectator'
          ? 'Spectator Pass'
          : 'Supporter Entry',
        price: wizardState.eventRegistration.entryFee,
        quantity: 1,
      });
    }

    // Add tournaments
    wizardState.tournaments.selectedIds.forEach(tournamentId => {
      const tournament = tournaments.find(t => t.id === tournamentId);
      if (tournament) {
        items.push({
          id: `tournament-${tournamentId}`,
          type: 'tournament',
          itemId: tournamentId,
          name: tournament.name,
          price: tournament.registrationFee,
          quantity: 1,
        });
      }
    });

    // Add addons
    wizardState.addons.selections.forEach(selection => {
      const addon = addons.find(a => a.id === selection.addonId);
      if (addon && selection.quantity > 0) {
        items.push({
          id: `addon-${selection.addonId}`,
          type: 'addon',
          itemId: selection.addonId,
          name: addon.name,
          price: addon.price,
          quantity: selection.quantity,
          variant: selection.variantData ? {
            name: String(selection.variantData.name || ''),
            data: selection.variantData,
          } : undefined,
        });
      }
    });

    const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const tax = Math.round(subtotal * (taxRate / 100));
    const total = subtotal + tax;

    onCartUpdate({
      items,
      subtotal,
      tax,
      total,
    });
  }, [wizardState, tournaments, addons, taxRate, onCartUpdate]);

  const handleCheckout = async () => {
    if (!isSignedIn || !user) {
      setError('Please sign in to complete your registration');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const userEmail = user.primaryEmailAddress?.emailAddress || '';
      const userName = [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined;

      // For free registrations, create directly in Supabase
      if (wizardState.cart.total === 0) {
        await createFreeRegistration({
          clerkUserId: user.id,
          userEmail,
          userName,
          entryType: wizardState.eventRegistration.entryType || 'free',
        });
        onComplete();
        return;
      }

      // For paid registrations, redirect to Stripe checkout
      const response = await createCheckoutSession({
        cart: wizardState.cart,
        userEmail,
        clerkUserId: user.id,
        userName,
      });

      // Redirect to Stripe Checkout
      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during checkout');
      setIsProcessing(false);
    }
  };

  const cart = wizardState.cart;

  if (cart.items.length === 0) {
    return (
      <div className="text-center py-12 space-y-4">
        <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground" />
        <p className="text-muted-foreground">Your cart is empty.</p>
        <p className="text-sm text-muted-foreground">
          Go back and select tournaments or add-ons, or continue with free registration.
        </p>
        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={onBack}>Back</Button>
          <Button onClick={handleCheckout}>Complete Free Registration</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-heading font-bold">Review & Checkout</h2>
        <p className="text-muted-foreground mt-2">
          Review your selections and complete your registration
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Your Cart
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {cart.items.map(item => (
            <div key={item.id} className="flex items-center justify-between py-2 border-b last:border-0">
              <div>
                <p className="font-medium">{item.name}</p>
                {item.quantity > 1 && (
                  <p className="text-sm text-muted-foreground">
                    Qty: {item.quantity} x {formatCurrency(item.price)}
                  </p>
                )}
                {item.variant && (
                  <p className="text-sm text-muted-foreground">
                    {item.variant.name}
                  </p>
                )}
              </div>
              <p className="font-semibold">{formatCurrency(item.price * item.quantity)}</p>
            </div>
          ))}

          <div className="pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatCurrency(cart.subtotal)}</span>
            </div>
            {cart.tax > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax ({taxRate}%)</span>
                <span>{formatCurrency(cart.tax)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>Total</span>
              <span>{formatCurrency(cart.total)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center gap-4">
        <Button variant="outline" onClick={onBack} disabled={isProcessing}>
          Back
        </Button>
        <Button onClick={handleCheckout} disabled={isProcessing} size="lg">
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : cart.total === 0 ? (
            'Complete Registration'
          ) : (
            `Pay ${formatCurrency(cart.total)}`
          )}
        </Button>
      </div>
    </div>
  );
}
